// @react
import React from 'react'
// @types
import type { ReactElement } from 'react'
import type {
  GetUserQuery,
  GetUserQueryVariables,
} from 'lib/graphql/graphqlTypes'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_USER } from 'graphql/queries/user.query'
// @libraries
import { makeStyles } from '@material-ui/core/styles'
// @components
import strings from 'lib/constants/strings'
import Icon from '@bit/atd.web.icons'
import Grid from '@bit/atd.web.grid'
import Spacer from '@bit/atd.web.spacer'
import colors from '@bit/atd.web.colors'
import Box from '@bit/atd.web.box'
import Button from '@bit/atd.web.button'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module common components
import useShowMore from 'modules/common/hooks/useShowMore'
import QueryError from 'modules/common/components/Errors/QueryError'

/**
 * 3.0.1.2.2 MyATD - Benefits Center - My Sites & Resource
 * https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3528%3A41884
 */

const useStyles = makeStyles(() => ({
  body: {
    paddingTop: '6px !important',
  },
}))

/**
 *
 * @constructor
 */
const MemberResources = (): ReactElement => {
  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  const { loading: isLoading, error: isError, data } = useQuery<
    GetUserQuery,
    GetUserQueryVariables
  >(GET_USER, { fetchPolicy: 'cache-first' })

  const { visibleItems, showMoreProps, ShowMore } = useShowMore({
    limit: 4,
    showMoreLimit: 30,
    showMore: true,
    items: data?.user?.membership?.benefitResources || [],
  })

  /******************************************
   * RENDERS
   ******************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  if (data && !isLoading && !data?.user?.membership?.benefitResources.length) {
    return <></>
  }

  const content = !isLoading ? (
    data?.user ? (
      <Grid container spacing={1}>
        {visibleItems.map((item: any, index) => {
          return (
            <div key={`${index}_${item.url}`}>
              <Grid item>
                <Button variant="secondary" size="small" href={item.url}>
                  <>
                    {item.title}
                    <Spacer size={4} />
                    <Icon
                      glyph="external"
                      size={16}
                      color={colors.brandOrange}
                    />
                  </>
                </Button>
              </Grid>

              {visibleItems.length > 1 && (
                <Grid item xs={12}>
                  <Spacer size={4} fullWidth />
                </Grid>
              )}
            </div>
          )
        })}

        <Grid item xs={12}>
          <ShowMore key="showMore" {...showMoreProps} />
        </Grid>
      </Grid>
    ) : (
      <>error</>
    )
  ) : (
    <SkeletonLoader />
  )

  return (
    <Box
      classNames={{
        body: classes.body,
      }}
      variant="subModule"
      header={{
        title: strings.PROFILE.MEMBERSHIP.SITES,
      }}
    >
      {content}
    </Box>
  )
}

export default MemberResources
